<template>
  <div ref="STAT_CLIENT">
    <section-header v-if="!hideTitle"
                    class="stat-client-header"
                    ref="H_LISTE_CLIENT"
                    :ressource="ressource"
                    :is-extended="extended"
                    :dialog="dialog"
                    :canAdd="canAdd"
                    :addLabel="addLabel"
                    :addDisabled="exportLoading"
                    :fiche=fiche
                    :showSearch=showSearch
                    :chat="extended && canExportSalesData"
                    :chatLabel="salesDataLabel"
                    :newChatItem="salesExportLoading"
                    :unreadChatCount="0"
                    @search=onSearchTextUpdated
                    @extended=setExtended
                    @callback=exportListe
                    @openChat=exportSalesData
                    @close-dialog=closeSection>
    </section-header>

    <div v-if="extended">
      <v-row class="ma-0 filters-row" no-gutters>
        <v-col v-for="(item, index) in headers.filter(h => getConfig(h.value).isFilterable)" cols="auto" :key="index" class="pr-2">
          <data-table-filter :headerValue="item.value"
                             :label="item.text"
                             :columnConfig="getConfig(item.value)"
                             :currentFilters="getFilters(item.value)"
                             @apply="applyFilters">
          </data-table-filter>
        </v-col>
      </v-row>
      <v-data-table :height="listHeight"
                    dense fixed-header
                    :headers="headers"
                    :items="items"
                    :items-per-page="-1"
                    :must-sort="false"
                    :custom-sort="() => 0"
                    :mobile-breakpoint="100"
                    item-key="id"
                    class="white"
                    no-data-text="Aucune donnée"
                    :disable-pagination="true"
                    :hide-default-footer="true"
                    return-object
                    @update:sortBy="onSortUpdate">
        <template #[`item.dateCreationCompte`]="{ item }">
          {{ formatDate(item.dateCreationCompte) }}
        </template>
        <template #[`item.abonnementDebut`]="{ item }">
          {{ formatDate(item.abonnementDebut) }}
        </template>
        <template #[`item.firstDsnDate`]="{ item }">
          {{ formatDate(item.firstDsnDate) }}
        </template>
        <template #[`item.hasProject`]="{ item }">
          {{ formatBoolean(item.hasProject) }}
        </template>
        <template #[`item.hasContrat`]="{ item }">
          {{ formatBoolean(item.hasContrat) }}
        </template>
        <template #[`item.hasSalarie`]="{ item }">
          {{ formatBoolean(item.hasSalarie) }}
        </template>
        <template #[`item.hasPaie`]="{ item }">
          {{ formatBoolean(item.hasPaie) }}
        </template>
        <template #[`item.ci98`]="{ item }">
          {{ formatBoolean(item.ci98) }}
        </template>
        <template #[`item.isDomtom`]="{ item }">
          {{ formatBoolean(item.isDomtom) }}
        </template>
        <template #[`item.disabled`]="{ item }">
          {{ formatBoolean(item.disabled) }}
        </template>
        <template #[`item.action`]="{ item }">
          <v-row class="m-0">
            <div class="col">
              <v-icon @click="sudo(item)">
                fas fa-right-to-bracket
              </v-icon>
            </div>
            <div v-if="item.etablissementId > 0" class="col">
              <v-icon @click.stop="updateItem(item)" size="22" color="border">fas fa-pencil</v-icon>
            </div>
          </v-row>
        </template>
        <template v-for="(item, index) in headers" v-slot:[`header.${item.value}`]>
          <div :key="index" style="display: flex; align-items: center;">
            <div><span>{{ item.text }}</span></div>
          </div>
        </template>
      </v-data-table>
      <v-row v-if="showCount" class="m-0">
        <v-col cols="11">
          <v-pagination v-if="pageCount > 1" v-model="page" :length="pageCount" :total-visible="10" class="pagination-color"></v-pagination>
        </v-col>
        <v-col class="text-right align-content-center">
          {{ filteredCount }}/{{ totalCount }}
        </v-col>
      </v-row>
    </div>
    <section-container :section="section"
                       :dialogOpen="dialogOpen"
                       :extended="true"
                       :fullscreen="fullscreen"
                       @closeDialog="closeDialog"
                       @saved="saved"
                       @updated="setUpdated">
    </section-container>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from "vue";
  // eslint-disable-next-line
  import { GenSection, RessourceGenSection } from '@/generated/ressources/generatedRessources';
  import {Fiches, Sections} from '@/generated/classes/AppSchema';
  import { FwkButtons, RessourceFwkButton, RessourceFwkSection, FwkSections } from '@/ressources/globalRessources';
  import SectionHeader from "@/framework/base/components/SectionHeader.vue";
  import { DateFormat } from "@/framework/base/helpers/DateFormat.ts";
  import Files, { DownloadData } from "@/generated/classes/Files.ts";
  import { getBlob } from "@/framework/files/helpers/FileTypes.ts";
  import { Extended } from "@/framework/base/classes/Schema.ts";
  import ListData from "@/extend/classes/ListData.ts";
  import ListBase from "@/framework/base/components/ListBase.ts";
  import StatClient from "@/generated/classes/StatClient.ts";
  import DataTableFilter from "@/framework/base/components/DataTableFilter.vue";
  import {storeEtablissement, storeSession} from "@/generated/stores";
  import { useRouter } from "vue-router";
  import SectionContainer from "@/framework/base/components/SectionContainer.vue";
  import Section from "@/framework/base/classes/Section.ts";
  import Salarie from "@/extend/classes/Salarie.ts";
  import Route from "@/extend/classes/Route.ts";
  import SectionRibExtend from "@/extend/sections/SectionRibExtend.vue";
  import SectionEtablissementCommercialExtend from "@/extend/sections/SectionEtablissementCommercialExtend.vue";
  import UserProfile, { EnumProfile } from "@/framework/base/classes/UserProfile";

  export default defineComponent({
    components: {SectionContainer, DataTableFilter, SectionHeader },
    extends: ListBase,
    props: {
      hideTitle: {
        type: Boolean,
      },
      extended: {
        type: Boolean,
        required: false,
      },
      dialog: {
        type: Boolean,
        required: false,
      },
      fiche: {
        type: String,
        required: true,
      },
    },

    data() {
      return {
        sectionName: Sections.STAT_CLIENT,
        listHeight: 'calc(100vh - 300px)',
        exportLoading: false,
        router: useRouter(),
        dialogOpen: false,
        section: new Section(),
        salesExportLoading: false,
      }
    },

    computed: {
      saveLabel() {
        return RessourceFwkButton(FwkButtons.ENREGISTRER).label;
      },

      errorTxt() {
        return RessourceFwkSection(FwkSections.ERROR_TEXT).label;
      },

      showSearch(): boolean {
        return true;
      },

      showCount(): boolean {
        return true;
      },

      canAdd(): boolean {
        return true;
      },

      addLabel(): string {
        return 'Exporter';
      },

      salesDataLabel(): string {
        if (this.salesExportLoading) {
          return 'Téléchargement...';
        }
        return 'Données des ventes';
      },

      canExportSalesData(): boolean {
        const user = storeSession.connectedUser;
        return user != null && UserProfile.hasProfile(user, EnumProfile.admin);
      },

      ressource() { return RessourceGenSection(GenSection.STAT_CLIENT); },

      headers(): any {
        return [
          { text: 'Action(s)', minWidth: '80px', value: 'action', cellClass: 'iterate-cell-class iterate-cell-action', sortable: false },
          { text: "Commercial", value: 'commercial', cellClass: '', sortable: true },
          { text: "etablissementId", value: 'etablissementId', cellClass: '', sortable: true },
          { text: "CI98", value: 'ci98', cellClass: '', sortable: true },
          { text: "DOM-TOM", value: 'isDomtom', cellClass: '', sortable: true, align: 'center' },
          { text: "Compte désactivé", value: 'disabled', cellClass: '', sortable: true, align: 'center' },
          { text: "refClient", value: 'refClient', cellClass: '', sortable: true },
          { text: "Prenom", value: 'prenomContact', cellClass: '', sortable: true, align: 'left' },
          { text: "Nom", value: 'nomContact', cellClass: '', sortable: true, align: 'left' },
          { text: "Email", value: 'email', cellClass: '', sortable: true },
          { text: "Numero", value: 'numero', cellClass: '', sortable: true, minWidth: '150px',align: 'left' },
          { text: "Raison Sociale", value: 'raisonSociale', cellClass: '', sortable: true },
          { text: "Siret", value: 'siret', cellClass: '', sortable: true, minWidth: '150px', align: 'left' },
          { text: "Date Creation Compte", value: 'dateCreationCompte', cellClass: '', sortable: true },
          { text: "Abonnement Souscrit", value: 'abonnementSouscrit', cellClass: '', sortable: true, align: 'left' },
          { text: "Abonnement Debut", value: 'abonnementDebut', cellClass: '', sortable: true },
          { text: "Date première DSN", value: 'firstDsnDate', cellClass: '', sortable: true },
          { text: "Projet", value: 'hasProject', cellClass: '', sortable: true, align: 'center' },
          { text: "Contrat", value: 'hasContrat', cellClass: '', sortable: true, align: 'center' },
          { text: "Salarie", value: 'hasSalarie', cellClass: '', sortable: true, align: 'center' },
          { text: "Paie", value: 'hasPaie', cellClass: '', sortable: true, align: 'center' },
          { text: "Nombre Etablissement", value: 'nombreEtablissement', cellClass: '', sortable: true, align: 'center' }
        ];
      },
      items(): unknown[] {
        return this.listData.reccords;
      },
      exportDisabled(): boolean {
        return this.listData.reccords.length > 0;
      },
    },
    created() {
      this.loadTable();
    },
    methods: {
      loadTable(): void {
        if (!this.loading) {
          this.loading = true;
          StatClient.list(this.filter).then((res) => {
            this.listData = new ListData();
            this.listData = res;
            this.pageNumber = res.currentPage;
            this.loading = false;
          });
          this.pageNumber = 1;
        }
      },
      formatDate(value: Date | null): string {
        return value ? DateFormat.getDateLabel(value) : '';
      },
      formatBoolean(value: boolean): string {
        if (value) {
          return 'Oui';
        } else {
          return 'Non';
        }
      },
      exportListe(): void {
        if (!this.exportLoading) {
          const data = new DownloadData();
          this.exportLoading = true;
          data.service = 'StatClient';
          data.type.extension = 'xlsx';
          data.data = JSON.parse(JSON.stringify(this.filter));

          const date = new Date();
          const year = date.toLocaleString("default", { year: "numeric" });
          const month = date.toLocaleString("default", { month: "2-digit" });
          const day = date.toLocaleString("default", { day: "2-digit" });
          const fileName = `${year}-${month}-${day}-${data.service}`; // set table name as default file name

          Files.download(data)
            .then((buffer) => {
              const blob = getBlob(buffer, data.type.extension);
              const file = window.URL.createObjectURL(blob);
              const docUrl = document.createElement('a');
              docUrl.setAttribute('href', file);
              docUrl.setAttribute('download', `${fileName}.${data.type.extension}`);
              document.body.appendChild(docUrl);
              docUrl.click();
              this.exportLoading = false;
            });
        }
      },
      exportSalesData(): void {
        if (!this.salesExportLoading) {
          const data = new DownloadData();
          this.salesExportLoading = true;
          data.service = 'Ventes';
          data.type.extension = 'xlsx';

          const date = new Date();
          const year = date.toLocaleString("default", { year: "numeric" });
          const month = date.toLocaleString("default", { month: "2-digit" });
          const day = date.toLocaleString("default", { day: "2-digit" });
          const fileName = `${year}-${month}-${day}-${data.service}`;

          Files.download(data)
            .then((buffer) => {
              const blob = getBlob(buffer, data.type.extension);
              const file = window.URL.createObjectURL(blob);
              const docUrl = document.createElement('a');
              docUrl.setAttribute('href', file);
              docUrl.setAttribute('download', `${fileName}.${data.type.extension}`);
              document.body.appendChild(docUrl);
              docUrl.click();
              this.salesExportLoading = false;
            });
        }
      },
      sudo(item: unknown) {
        const adminNom = storeSession.connectedUser?.nom ?? '';
        const adminPrenom = storeSession.connectedUser?.prenom ?? '';
        storeSession.sudo(item.id).then((res) => {
          if (res) {
            sessionStorage.setItem('resynchSudoAdminNom', adminNom);
            sessionStorage.setItem('resynchSudoAdminPrenom', adminPrenom);
            sessionStorage.setItem('resynchSudoToken', storeSession.httpToken);
            this.router.push({ name: 'Dashboard' });
            setTimeout(() => window.location.reload(), 1000)
          }
        })
      },
      setExtended(extended: Extended): void {
        this.$emit('extended', extended);
      },

      updateItem(item: StatClient) : void {
        if (item.etablissementId) {
          // A client can have several establishments. Keep the selected contact
          // metadata local to this editor instead of mutating the shared store item.
          const etablissement = storeEtablissement
            .getEtablissement(parseInt(item.etablissementId))
            .cloneEtablissement();
          etablissement.userId = item.id;
          etablissement.userDisabled = item.disabled;
          this.section = new Section();
          this.section.component = SectionEtablissementCommercialExtend;
          this.section.ressource = this.ressource;
          this.section.picto = 'SectionIdentitePicto';
          this.section.data = etablissement;
          this.section.type = 'etablissement';
          this.section.autoSave = false;
          this.dialogOpen = true;
        }
      },

      closeSection(): void {
        this.$emit('closeDialog');
      },
      saved(): void {
        this.loadTable();
      },
      closeDialog(): void {
        this.dialogOpen = false;
      }
    }
  })
</script>

<style scoped>
  .stat-client-header :deep(.pr-2) {
    padding-left: 8px;
  }

  .stat-client-header :deep(.pr-2 .v-icon),
  .stat-client-header :deep(.pr-2 .v-row > div:last-child) {
    display: none;
  }

  .stat-client-header :deep(.pr-2 .v-btn .v-row) {
    margin: 0;
  }
</style>
